// src/ui/FileMenu.js
// File menu bar component. Provides New/Open/Save/Save As/Export actions
// and shows the dirty indicator + project name.

import { t, onLocaleChange } from '../i18n.js';

export class FileMenu {
  /**
   * @param {HTMLElement} rootEl - the #file-menu nav element
   * @param {import('../project/ProjectManager.js').ProjectManager} projectManager
   */
  constructor(rootEl, projectManager) {
    this.root = rootEl;
    this.pm = projectManager;

    // Wire action buttons
    this.root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      e.stopPropagation();
      this._handleAction(btn.dataset.action);
    });

    // Close dropdown on outside click
    this._clickHandler = (e) => {
      if (!this.root.contains(e.target)) {
        this.root.querySelector('.file-menu-dropdown')?.classList.remove('open');
      }
    };
    document.addEventListener('click', this._clickHandler);

    // Toggle dropdown
    this.root.querySelector('.file-menu-trigger')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.root.querySelector('.file-menu-dropdown')?.classList.toggle('open');
    });

    // Dirty flag subscription
    this.pm.onDirtyChange((dirty) => {
      const el = this.root.querySelector('.file-menu-dirty');
      if (el) el.classList.toggle('hidden', !dirty);
    });

    // Update project name display
    this._nameEl = this.root.querySelector('.file-menu-name');
    this._updateName();

    // Keyboard shortcuts
    this._bindShortcuts();
  }

  _handleAction(action) {
    // Close dropdown after any action
    this.root.querySelector('.file-menu-dropdown')?.classList.remove('open');

    switch (action) {
      case 'new': this.pm.newProject().then(() => this._updateName()); break;
      case 'open': this.pm.openProject().then(() => this._updateName()); break;
      case 'save': this.pm.saveProject().then(() => this._updateName()); break;
      case 'save-as': this.pm.saveProjectAs().then(() => this._updateName()); break;
      case 'export': this.pm.exportWithSamples(); break;
    }
  }

  _updateName() {
    if (this._nameEl) {
      this._nameEl.textContent = this.pm.projectName;
    }
  }

  _bindShortcuts() {
    this._keydownHandler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      switch (e.code) {
        case 'KeyN':
          e.preventDefault();
          this.pm.newProject().then(() => this._updateName());
          break;
        case 'KeyO':
          e.preventDefault();
          this.pm.openProject().then(() => this._updateName());
          break;
        case 'KeyS':
          e.preventDefault();
          if (e.shiftKey) {
            this.pm.saveProjectAs().then(() => this._updateName());
          } else {
            this.pm.saveProject().then(() => this._updateName());
          }
          break;
      }
    };
    document.addEventListener('keydown', this._keydownHandler);
  }

  dispose() {
    document.removeEventListener('click', this._clickHandler);
    if (this._keydownHandler) document.removeEventListener('keydown', this._keydownHandler);
  }
}
